import type { FilterGroup, FilterValue } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { MRT_ColumnFiltersState } from 'material-react-table'
import type {
  FilterOption,
  MrtColumnConfig,
  MrtFilterOperator,
  MultiStepFilterValue,
  MultiStepPathEntry,
} from '../mrtCollectionTypes'
import { readStoredFilterValue } from './filterOperator'

const FILTER_INSTANCE_SEPARATOR = '#'
const MAX_LISTED_VALUES = 3

export const isMultiStepFilter = <T>(column: MrtColumnConfig<T>): boolean => column.filter?.variant === 'multi-step'

// Only multi-step filters can be added more than once today, so the same subject can be filtered on twice.
export const canHaveMultipleFilters = <T>(column: MrtColumnConfig<T>): boolean => isMultiStepFilter(column)

/** The column a chip belongs to. A column that can have multiple filters gives each one its own id. */
export const columnIdOfFilter = (filterId: string): string => filterId.split(FILTER_INSTANCE_SEPARATOR)[0]

export const nextFilterInstanceId = (columnId: string, filterIds: string[]): string => {
  const taken = new Set(filterIds)
  let instance = 1
  while (taken.has(`${columnId}${FILTER_INSTANCE_SEPARATOR}${instance}`)) instance++
  return `${columnId}${FILTER_INSTANCE_SEPARATOR}${instance}`
}

export const asMultiStepValue = (value: unknown): MultiStepFilterValue | undefined => {
  const candidate = value as MultiStepFilterValue | undefined
  return Array.isArray(candidate?.path) && Array.isArray(candidate?.values) ? candidate : undefined
}

/** One chip as a backend per-chip filter group: the chip's value object, with is / is not as In / NotIn. */
export const chipFilterGroups = (name: string, chipValue: object, operator: MrtFilterOperator): FilterGroup[] => [
  {
    name,
    filters: [
      {
        operator: operator === 'isNot' ? Operators.NotIn : Operators.In,
        // FilterValue has no object shape: only the per-chip filters take one, and widening the shared type
        // would ripple through every consumer that assumes a primitive.
        value: chipValue as unknown as FilterValue,
      },
    ],
  },
]

export const multiStepPathOf = (stored: unknown): MultiStepPathEntry[] =>
  asMultiStepValue(readStoredFilterValue(stored).value)?.path ?? []

const isSamePath = (left: MultiStepPathEntry[], right: MultiStepPathEntry[]): boolean =>
  left.length === right.length && left.every((entry, index) => entry.value === right[index].value)

/** The column's chip on exactly this path, so adding the same filter again edits that chip instead. */
export const findFilterOnSamePath = (
  columnId: string,
  path: MultiStepPathEntry[],
  columnFilters: MRT_ColumnFiltersState,
): MRT_ColumnFiltersState[number] | undefined =>
  path.length
    ? columnFilters.find(
        filter => columnIdOfFilter(filter.id) === columnId && isSamePath(multiStepPathOf(filter.value), path),
      )
    : undefined

/**
 * A column's chip on the value's path set to the value, or a new chip when the column has none on that path,
 * so the same subject can be filtered on twice but never shows two chips for one path.
 */
export const addFilterInstance = (
  columnFilters: MRT_ColumnFiltersState,
  columnId: string,
  value: unknown,
): MRT_ColumnFiltersState => {
  const existingId = findFilterOnSamePath(columnId, multiStepPathOf(value), columnFilters)?.id
  if (existingId) return columnFilters.map(filter => (filter.id === existingId ? { id: existingId, value } : filter))
  const id = nextFilterInstanceId(
    columnId,
    columnFilters.map(filter => filter.id),
  )
  return [...columnFilters, { id, value }]
}

/**
 * The filters that narrow a multi-step editor's counts: all but the chip being edited, and the chip already on
 * the chosen path, which applying replaces.
 */
export const otherColumnFilters = (
  columnId: string,
  path: MultiStepPathEntry[],
  columnFilters: MRT_ColumnFiltersState,
  editedFilterId?: string,
): MRT_ColumnFiltersState => {
  const replacedFilterId = findFilterOnSamePath(columnId, path, columnFilters)?.id
  return columnFilters.filter(filter => filter.id !== editedFilterId && filter.id !== replacedFilterId)
}

/** Values in the order the options list them, which is the scale order, not the order they were ticked in. */
export const sortValuesByOptions = (values: FilterOption[], options: FilterOption[]): FilterOption[] => {
  const position = (value: FilterOption): number => {
    const index = options.findIndex(option => option.value === value.value)
    return index === -1 ? options.length : index
  }
  return [...values].sort((left, right) => position(left) - position(right))
}

/** "Low or Medium", shortened after three values to the first two plus a count: "Low, Medium +2". */
export const summariseMultiStepValues = (values: FilterOption[], orWord: string): string =>
  values.length > MAX_LISTED_VALUES
    ? `${values[0].label}, ${values[1].label} +${values.length - 2}`
    : values.map(value => value.label).join(` ${orWord} `)

/**
 * The chip reads like the other chips, its choices standing in for the column label: subject, then qualifiers,
 * then "<operator> <values>", e.g. "Climate change · Supply chain: is Robust". A choice appended to the subject
 * reads with it, "Baseline 2026: is Level 1", so two chips on one subject read apart. With a leading subject
 * every choice is a qualifier after it: "Country risk · Forced labor: is High".
 */
export const getMultiStepChipLabel = (
  value: unknown,
  operatorLabel: string,
  orWord: string,
  leadingSubject?: string,
): string => {
  const multiStepValue = asMultiStepValue(value)
  if (!multiStepValue) return ''
  const shownChoices = multiStepValue.path.filter(entry => !entry.isOmittedFromChip)
  const subjectLabel = leadingSubject ?? shownChoices[0]?.label
  const choices = leadingSubject ? shownChoices : shownChoices.slice(1)
  if (!subjectLabel) return ''
  const appended = choices.filter(entry => entry.isAppendedToSubject).map(entry => entry.label)
  const qualifiers = choices.filter(entry => !entry.isAppendedToSubject).map(entry => entry.label)
  const choicesText = [[subjectLabel, ...appended].join(' '), ...qualifiers].join(' · ')
  return `${choicesText}: ${operatorLabel} ${summariseMultiStepValues(multiStepValue.values, orWord)}`
}
